import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  BarChart, Bar, Legend,
} from 'recharts';
import {
  TrendingUp, TrendingDown, DollarSign, ShoppingBag, Receipt, RotateCcw, Crown, Download,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANGE_OPTIONS = [
  { value: 7, label: '7d', period: 'day' },
  { value: 30, label: '30d', period: 'day' },
  { value: 90, label: '90d', period: 'week' },
  { value: 365, label: '1y', period: 'month' },
];

const formatMoney = (n) =>
  `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const Delta = ({ pct }) => {
  if (pct === null || pct === undefined) {
    return <span className="text-xs text-gray-500">vs prev: —</span>;
  }
  const positive = pct >= 0;
  const Icon = positive ? TrendingUp : TrendingDown;
  return (
    <span className={`inline-flex items-center gap-1 text-xs ${positive ? 'text-green-400' : 'text-red-400'}`}>
      <Icon className="w-3 h-3" />
      {positive ? '+' : ''}{pct}% vs prev
    </span>
  );
};

const KPI = ({ label, value, icon: Icon, deltaPct, accent = 'text-blue-400', subtext }) => (
  <Card className="bg-gray-800 border-gray-700">
    <CardContent className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs text-gray-400 uppercase tracking-wide">{label}</p>
          <p className="text-2xl font-bold text-white mt-1">{value}</p>
          <div className="mt-1">
            {deltaPct !== undefined ? <Delta pct={deltaPct} /> : null}
            {subtext && <span className="text-xs text-gray-500 block mt-0.5">{subtext}</span>}
          </div>
        </div>
        <Icon className={`w-9 h-9 opacity-30 ${accent}`} />
      </div>
    </CardContent>
  </Card>
);

const AdminAnalytics = () => {
  const [rangeIdx, setRangeIdx] = useState(1); // 30d default
  const [summary, setSummary] = useState(null);
  const [trend, setTrend] = useState({ points: [] });
  const [topProducts, setTopProducts] = useState({ products: [] });
  const [loading, setLoading] = useState(true);

  const range = RANGE_OPTIONS[rangeIdx];

  const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

  const downloadCsv = async (endpoint, defaultName) => {
    try {
      const response = await axios.get(`${API_URL}${endpoint}`, {
        headers: auth(),
        responseType: 'blob',
      });
      // Try to grab filename from Content-Disposition, fall back to default
      const cd = response.headers['content-disposition'] || '';
      const match = /filename="?([^";]+)"?/i.exec(cd);
      const filename = match ? match[1] : defaultName;

      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error('Failed to export CSV');
      console.error(e);
    }
  };

  useEffect(() => {
    let cancelled = false;
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [s, r, p] = await Promise.all([
          axios.get(`${API_URL}/api/admin/analytics/summary?days=${range.value}`, { headers: auth() }),
          axios.get(`${API_URL}/api/admin/analytics/revenue?period=${range.period}&days=${range.value}`, { headers: auth() }),
          axios.get(`${API_URL}/api/admin/analytics/top-products?days=${range.value}&limit=10`, { headers: auth() }),
        ]);
        if (cancelled) return;
        setSummary(s.data);
        setTrend(r.data);
        setTopProducts(p.data);
      } catch (e) {
        toast.error('Failed to load analytics');
        console.error(e);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchAll();
    return () => { cancelled = true; };
  }, [rangeIdx]);

  const chartData = useMemo(
    () => trend.points.map((p) => ({
      bucket: p.bucket,
      net: p.net,
      gross: p.gross,
      refunded: p.refunded,
    })),
    [trend.points]
  );

  return (
    <div className="p-8 space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white mb-1">Analytics</h1>
          <p className="text-gray-400">Revenue, orders, and top sellers — archived & refunds excluded.</p>
        </div>

        <div className="flex items-center gap-3 flex-wrap">
          {/* Export buttons */}
          <div className="inline-flex gap-2">
            <button
              onClick={() =>
                downloadCsv(
                  `/api/admin/analytics/export/revenue.csv?period=${range.period}&days=${range.value}`,
                  'revenue.csv'
                )
              }
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 text-gray-200 rounded-lg hover:bg-gray-700 transition-colors"
              data-testid="export-revenue-csv"
            >
              <Download className="w-4 h-4" />
              Revenue CSV
            </button>
            <button
              onClick={() =>
                downloadCsv(
                  `/api/admin/analytics/export/orders.csv?days=${range.value}`,
                  'orders.csv'
                )
              }
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-800 border border-gray-700 text-gray-200 rounded-lg hover:bg-gray-700 transition-colors"
              data-testid="export-orders-csv"
            >
              <Download className="w-4 h-4" />
              Orders CSV
            </button>
          </div>

          <div className="inline-flex bg-gray-800 border border-gray-700 rounded-lg p-1">
            {RANGE_OPTIONS.map((opt, idx) => (
              <button
                key={opt.value}
                onClick={() => setRangeIdx(idx)}
                className={`px-4 py-1.5 rounded text-sm font-medium transition-colors ${
                  rangeIdx === idx ? 'bg-blue-500 text-white' : 'text-gray-300 hover:bg-gray-700'
                }`}
                data-testid={`range-${opt.label}`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI
          label="Net Revenue"
          value={formatMoney(summary?.current?.net)}
          icon={DollarSign}
          accent="text-green-400"
          deltaPct={summary?.deltas?.net_pct}
          subtext={`Gross ${formatMoney(summary?.current?.gross)}`}
        />
        <KPI
          label="Orders"
          value={summary?.current?.orders ?? 0}
          icon={ShoppingBag}
          accent="text-blue-400"
          deltaPct={summary?.deltas?.orders_pct}
        />
        <KPI
          label="Avg Order Value"
          value={formatMoney(summary?.current?.aov)}
          icon={Receipt}
          accent="text-purple-400"
          deltaPct={summary?.deltas?.aov_pct}
        />
        <KPI
          label="Refund Rate"
          value={`${summary?.refund_rate_pct ?? 0}%`}
          icon={RotateCcw}
          accent="text-orange-400"
          subtext={`${summary?.refunded_count ?? 0} refunds · ${formatMoney(summary?.current?.refunded)}`}
        />
      </div>

      {/* Revenue Trend */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white">Revenue trend</h2>
            <span className="text-xs text-gray-500">
              Bucketed by {range.period} · last {range.value} days
            </span>
          </div>
          {loading ? (
            <div className="h-72 flex items-center justify-center text-gray-500">Loading...</div>
          ) : chartData.length === 0 ? (
            <div className="h-72 flex items-center justify-center text-gray-500">
              No paid orders in this period yet.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -10, bottom: 0 }}>
                <defs>
                  <linearGradient id="netFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.4} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="bucket" stroke="#9ca3af" fontSize={11} />
                <YAxis stroke="#9ca3af" fontSize={11} tickFormatter={(v) => `$${v}`} />
                <Tooltip
                  contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 6 }}
                  labelStyle={{ color: '#fff' }}
                  formatter={(value, name) => [formatMoney(value), name]}
                />
                <Area type="monotone" dataKey="net" stroke="#22c55e" fill="url(#netFill)" strokeWidth={2} name="Net Revenue" />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Top Products */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-white flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-400" />
              Top products
            </h2>
            <span className="text-xs text-gray-500">By units sold · last {range.value} days</span>
          </div>

          {loading ? (
            <div className="h-72 flex items-center justify-center text-gray-500">Loading...</div>
          ) : topProducts.products.length === 0 ? (
            <div className="h-72 flex items-center justify-center text-gray-500">
              No sales recorded in this period.
            </div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart data={topProducts.products} layout="vertical" margin={{ left: 100, right: 30 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis type="number" stroke="#9ca3af" fontSize={11} />
                  <YAxis type="category" dataKey="title" stroke="#9ca3af" fontSize={11} width={140} />
                  <Tooltip
                    contentStyle={{ background: '#1f2937', border: '1px solid #374151', borderRadius: 6 }}
                    labelStyle={{ color: '#fff' }}
                    formatter={(value, name) => name === 'revenue' ? [formatMoney(value), 'Revenue'] : [value, 'Units sold']}
                  />
                  <Legend wrapperStyle={{ color: '#9ca3af' }} />
                  <Bar dataKey="units_sold" fill="#3b82f6" name="Units sold" radius={[0, 4, 4, 0]} />
                  <Bar dataKey="revenue" fill="#10b981" name="Revenue" radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>

              <div className="mt-4 overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-400 uppercase tracking-wide">
                      <th className="px-3 py-2">#</th>
                      <th className="px-3 py-2">Product</th>
                      <th className="px-3 py-2 text-right">Units</th>
                      <th className="px-3 py-2 text-right">Revenue</th>
                      <th className="px-3 py-2 text-right">Orders</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-700">
                    {topProducts.products.map((p, i) => (
                      <tr key={p.product_id || p.title} className="hover:bg-gray-750">
                        <td className="px-3 py-2 text-gray-400">{i + 1}</td>
                        <td className="px-3 py-2 text-white">{p.title}</td>
                        <td className="px-3 py-2 text-right text-white">{p.units_sold}</td>
                        <td className="px-3 py-2 text-right text-green-400">{formatMoney(p.revenue)}</td>
                        <td className="px-3 py-2 text-right text-gray-400">{p.order_count}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminAnalytics;
